<template>
  <BLayout :colors="$site.colors" :header-back-route="homeRoute">
    <BContainer>
      <div class="max-w-sm mx-auto">
        <h1 class="text-primary font-semibold text-xl mb-2 text-center">
          {{ $t('pages.customer.login.index.title') }}
        </h1>
        <p  v-if="$site.settings.allowNewRegistrations" class="text-sm text-secondary/75 mb-6 text-center">
          {{ $t('pages.customer.login.index.noAccount') }} <NuxtLink class="text-primary" to="/customer/register">
            {{ $t('pages.customer.login.index.signUp') }}
          </NuxtLink>
        </p>

        <BErrors :errors="errors" />

        <BCard class="mb-6">
          <form @submit.prevent="submit">
            <div v-if="mode === `email` || mode === `password`">
              <UFormGroup>
                <UInput v-model="email" size="lg" flat :ui="{ leading: { padding: { lg: 'ps-16'}}}">
                  <template #leading>
                    <p class="text-secondary/50 mr-3 pr-3 border-r border-secondary whitespace-nowrap flex-shrink-0 text-sm">
                      {{ $t('general.email') }}
                    </p>
                  </template>
                </UInput>
              </UFormGroup>
            </div>

            <div v-if="mode === `email`" class="mt-6">
              <UButton block size="md" @click.prevent="enterPassword">
                {{ $t('pages.customer.login.index.enterPassword') }}
              </UButton>

              <div class="flex items-center my-2 px-2 justify-center opacity-75">
                <span class="px-2 text-sm text-secondary">{{ $t('general.or') }}</span>
              </div>

              <UButton block size="md" :loading="passwordless.loadingCustomers" @click="findCustomer">
                {{ $t('pages.customer.login.index.sendCode') }}
              </UButton>

              <div class="mt-5 text-secondary">
                <small>{{ $t('pages.customer.login.index.cantRemember') }}</small>
                {{ ' ' }}
                <small>
                  <NuxtLink class="bookapp-action-link text-primary" :to="{ path: '/customer/recovery' }">
                    {{ $t('pages.customer.login.index.passwordReset') }} »
                  </NuxtLink>
                </small>
              </div>
            </div>

            <!-- Enter password -->
            <div v-if="mode === `password`" class="mt-3">
              <UFormGroup>
                <UInput v-model="password" type="password" size="lg" :ui="{ leading: { padding: { lg: 'ps-24'}}}">
                  <template #leading>
                    <p class="text-secondary/50 mr-3 pr-3 border-r border-secondary text-sm">
                      {{ $t('pages.customer.login.index.password') }}
                    </p>
                  </template>
                </UInput>
              </UFormGroup>

              <UButton type="submit" class="mt-6" block :loading="loggingIn">
                {{ $t('pages.customer.login.index.login') }}
              </UButton>
            </div>

            <div v-if="mode === `customer`">
              <p class="text-sm text-secondary">
                {{ $t('pages.customer.login.index.multipleAccountSelect', { email }) }}
              </p>

              <div class="mb-3">
                <UButton
                  v-for="customer in passwordless.customers"
                  :key="customer.id"
                  class="mb-1 w-full"
                  :loading="passwordless.requestingCode && passwordless.customer === customer"
                  :disabled="passwordless.requestingCode"
                  @click="requestCode(customer)"
                >
                  {{ customer.displayName }}
                </UButton>
              </div>
            </div>

            <div v-if="mode === `code`">
              <p class="text-secondary mb-4 text-sm">
                {{ $t('pages.customer.login.index.weveSentYouACode', { email: passwordless.customer?.email }) }}
              </p>

              <div>
                <ElementsOtpInput v-model="code" @complete="loginWithCode" />
              </div>
            </div>

            <div v-if="mode !== `email`">
              <UButton variant="soft" class="mt-6" :loading="loggingIn" block @click="backToEmail">
                {{ $t('general.back') }}
              </UButton>
            </div>
          </form>
        </BCard>
      </div>
    </BContainer>
  </BLayout>
</template>

<script setup lang="ts">
import type { Customer } from '@@/types/api'

const { $api, $auth, $site, $i18n } = useNuxtApp()

definePageMeta({
  tabs: ['user'],
  layout: 'default',
})

const email = ref<string>('')
const password = ref<string>('')
const mode = ref<'email' | 'password' | 'customer' | 'code'>('email')

const passwordless = ref<{
  loadingCustomers: boolean
  requestingCode: any
  loggingIn: boolean
  notFound: boolean
  customers: Customer[] | null
  customer: Customer | null
  code: any
  token: any
}>({
  loadingCustomers: false,
  requestingCode: null,
  loggingIn: false,
  notFound: false,
  customers: null,
  customer: null,
  code: null,
  token: null
})

const errors = ref<string[] | null>(null)
const loggingIn = ref<boolean>(false)
const code = ref('')

const homeRoute = computed(() => {
  const locationStore = useLocationStore()

  return locationStore?.homeRoute || { path: '/' }
})

const submit = () => {
  if (mode.value === 'email') {
    enterPassword()
  } else if (mode.value === 'password') {
    loginWithPassword()
  }
}

const enterPassword = () => {
  errors.value = null

  if ((email.value || '').length === 0) {
    errors.value = [$i18n.t('pages.customer.login.index.validateEmail')]
    return
  }

  mode.value = 'password'
}

const backToEmail = () => {
  errors.value = null

  mode.value = 'email'
}

const findCustomer = async () => {
  errors.value = null

  if ((email.value || '').length === 0) {
    errors.value = [$i18n.t('pages.customer.login.index.validateEmail')]
    return
  }

  passwordless.value.loadingCustomers = true
  const response = await $api.customer.find(email.value)

  if (response && response.success) {
    if (!response.found) {
      passwordless.value.notFound = true

      errors.value = [$i18n.t('pages.customer.login.index.noCustomersForEmail')]
    } else if (response.customers?.length === 1) {
      await requestCode(response.customers[0])
    } else if (response.customers) {
      passwordless.value.customers = response.customers

      mode.value = 'customer'
    }
  } else if (response?.errors) {
    errors.value = response.errors
  }

  passwordless.value.loadingCustomers = false
}


const requestCode = async (customer: Customer) => {
  passwordless.value.requestingCode = false
  passwordless.value.customer = customer

  try {
    if (email.value) {
      const response = await $auth.requestLoginCode(
        customer,
        email.value
      )

      if (response.token) {
        mode.value = 'code'
  
        passwordless.value.token = response.token
      }
    }
  } catch (error: any) {
    console.log(error)
    errors.value = error?.errors
  }

  passwordless.value.requestingCode = false
}

const loginWithPassword = async () => {
  errors.value = null

  if ((email.value || '').length === 0) {
    errors.value = [$i18n.t('pages.customer.login.index.validateEmail')]
    return
  }

  if ((password.value || '').length === 0) {
    errors.value = [$i18n.t('pages.customer.login.index.validatePassword')]
    return
  }

  loggingIn.value = true

  try {
    if (email.value && password.value) {
      const response = await $auth.loginWithPassword(
        email.value,
        password.value
      )

      if (response.token) {
        navigateTo({ path: '/customer' })
      }

      if (response.errors) {
        errors.value = response.errors
      }
    }
  } catch (error: any) {
    errors.value = error.errors
  }

  loggingIn.value = false
}

const loginWithCode = async (code: string) => {
  if ((code || '').length !== 6) {
    errors.value = [$i18n.t('pages.customer.login.index.validateCode')]
    return
  }

  passwordless.value.loggingIn = true

  try {
    if (email.value) {
      const response = await $auth.loginWithCode(
        passwordless.value.customer,
        email.value,
        passwordless.value.token,
        code
      )
  
      if (response.token) {
        navigateTo({ path: '/customer' })
      }

      if (response.errors) {
        errors.value = response.errors
      }
    }
  } catch (error: any) {
    console.log(error)
    errors.value = error.errors
  }

  passwordless.value.loggingIn = false
}
</script>